import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';
import { errorMessage } from '../services/finesService';
import { useAiAssistant, assistantHtml } from '../hooks/useAiAssistant';
import contactsService, { formatPhone, whatsappUrl, phoneDigits } from '../services/contactsService';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import Card from '../ui/Card';
import Button from '../ui/Button';
import StatusBadge from '../ui/Status/StatusBadge';
import CustomerProfileModal from './CustomerProfileModal';
import '../ui/ds.css';

/**
 * "Ask Yelo": a phone, Emirates ID, name or contract finds the customer in every branch; questions go to the engine's
 * assistant. On the Home page (Overview) and on its own page, AI Assistant (owner, 5 Oct 2026: "ازاى ادى صلاحية المساعد
 * الذكي لحد من غير ما يظهر باقى الأوفر فيو ؟") - moved here from the Home page as it was.
 */
const KEEP_MS = 60 * 60 * 1000;     // the last answer is kept one hour (owner: "خليه يحفظ ساعه"), then the box opens empty

export default function AskYelo() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { showToast } = useModal();
    const [aiResponse, setAiResponseState] = useState(() => {
        // the last engine answer stays while moving between pages - for KEEP_MS only (owner, 10 Oct 2026: a plate
        // asked 3 days before was still there each time the page opened)
        try {
            const kept = JSON.parse(sessionStorage.getItem('yelo.ai.last') || 'null');
            if (kept && kept.at && Date.now() - kept.at < KEEP_MS) return kept.value;
            sessionStorage.removeItem('yelo.ai.last');
        } catch (e) { /* storage blocked or an old entry: nothing kept */ }
        return null;
    });
    const setAiResponse = useCallback((value) => {
        setAiResponseState(value);
        try {
            if (value && value.type === 'html') sessionStorage.setItem('yelo.ai.last', JSON.stringify({ at: Date.now(), value }));
            else sessionStorage.removeItem('yelo.ai.last');
        } catch (e) { /* storage blocked: nothing to keep */ }
    }, []);
    const [question, setQuestion] = useState('');
    const [profileSeed, setProfileSeed] = useState(null); // Customer profile opened from Ask Yelo

    // Initialize AI Brain
    const { processQuery, askEngine } = useAiAssistant(null, currentUser);

    // --- Assistant ---
    const runEngine = (asked) => {
        // a contract / plate answers at once, a live statement takes 10-20 s
        setAiResponse({ text: 'Thinking… a live statement takes 10-20 seconds', type: 'wait', options: [] });
        setQuestion('');
        askEngine(asked)
            .then((r) => setAiResponse({ text: r?.text || '', type: 'html', options: [] }))
            .catch((err) => setAiResponse({ text: errorMessage(err), type: 'error', options: [] }));
    };

    const pickOption = (opt) => {
        if (opt.seed) { setProfileSeed(opt.seed); return; }
        if (opt.engine) { runEngine(opt.engine); return; }
        navigate(opt.action);
        setAiResponse(null);
    };

    const ask = async (e) => {
        e.preventDefault();
        const asked = question.trim();
        if (!asked) return;
        // customers are searched in the contacts store: make sure it is in memory first
        let contactsOk = contactsService.isLoaded();
        if (!contactsOk) {
            setAiResponse({ text: 'Loading customers…', type: 'wait', options: [] });
            contactsOk = await contactsService.ensureLoaded();
        }
        // a failed load is said as such, not as "no customer with this number" (ADM-22)
        let response = processQuery(asked, { contactsOk });
        if (response && typeof response.then === 'function') {
            // a contract number is checked in the stores before any button is offered
            setAiResponse({ text: 'Checking the contract…', type: 'wait', options: [] });
            response = await response;
        }

        if (response.type === 'error') {
            setAiResponse({ text: response.text, type: 'error', options: [] });
        } else if (response.type === 'choice' || response.type === 'customers' || response.type === 'carContract') {
            setAiResponse(response);
        } else if (response.type === 'customerProfile') {
            setAiResponse({ text: response.text, type: 'text', options: [] });
            setProfileSeed(response.seed);
        } else if (response.type === 'engine') {
            runEngine(asked);
        } else {
            showToast(response.text, 'info');
            setAiResponse(null);
            setQuestion('');
        }
    };

    return (
        <>
                    <Card padding="none" title="Ask Yelo" subtitle="A phone, Emirates ID, name or contract finds the customer in every branch; questions go to the assistant">
                        <div className="dash-ask">
                            <form onSubmit={ask} className="dash-ask__form">
                                <label htmlFor="dash-ask-input" className="sr-only">Ask the assistant</label>
                                <input
                                    id="dash-ask-input"
                                    className="ds-input"
                                    dir="auto"
                                    placeholder="e.g. 0501234567 · Mohamed Ali · find car 41287 · how much on contract …"
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    autoComplete="off"
                                />
                                <Button type="submit" variant="primary" leadingIcon={<Icon glyph={ICONS.arrowRight} size="sm" />}>Ask</Button>
                            </form>

                            {aiResponse && (
                                <div className="dash-ask__answer" aria-live="polite">
                                    {aiResponse.type === 'html' ? (
                                        <div
                                            dir="auto"
                                            className="dash-ask__html"
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{ __html: assistantHtml(aiResponse.text) }}
                                        />
                                    ) : (
                                        <div dir="auto" className={`dash-ask__text ${aiResponse.type === 'error' ? 'is-error' : ''}`}>
                                            {aiResponse.type === 'wait' && <span className="ds-spinner ds-spinner--sm" aria-hidden="true" />}
                                            {aiResponse.text}
                                        </div>
                                    )}
                                    {/* a car's number: the contract it is on NOW, first and clearly (owner, 12 Oct 2026) */}
                                    {aiResponse.type === 'carContract' && (
                                        <ul className="dash-ask__cars">
                                            {aiResponse.cars.map((car) => {
                                                const k = car.open || car.last;
                                                const enc = encodeURIComponent(k.no);
                                                return (
                                                    <li key={car.plate} className={`dash-ask__car ${car.open ? 'is-open' : ''}`}>
                                                        <div className="dash-ask__car-head">
                                                            <Icon glyph={ICONS.car} size="sm" />
                                                            <span className="dash-ask__car-plate">{car.plate}</span>
                                                            {car.open
                                                                ? <StatusBadge status="open" label="On a contract now" />
                                                                : <StatusBadge tone="neutral" label="Not on a contract now" />}
                                                        </div>
                                                        <div className="dash-ask__car-label">{car.open ? 'Contract' : 'Last contract'}</div>
                                                        <div className="dash-ask__car-no">{k.no}</div>
                                                        {/* an Arabic name turned the phone round ("966…+"): each keeps its own direction */}
                                                        <div className="dash-ask__car-line">
                                                            <b><bdi>{k.customer || 'Customer'}</bdi></b>
                                                            {/* the phone opens WhatsApp, in WhatsApp's green (owner, 12 Oct 2026) */}
                                                            {phoneDigits(k.phone) ? (
                                                                <> · <a className="dash-ask__wa" href={whatsappUrl(phoneDigits(k.phone))} target="_blank" rel="noopener noreferrer"
                                                                    title="Open in WhatsApp"><bdi dir="ltr">{formatPhone(phoneDigits(k.phone))}</bdi></a></>
                                                            ) : null}
                                                        </div>
                                                        <div className="dash-ask__car-line">
                                                            {[k.branch, car.open ? `from ${k.from}` : `${k.from} → ${k.to}`, car.open && k.to ? `return ${k.to}` : '', car.open ? '' : k.status].filter(Boolean).join(' · ')}
                                                        </div>
                                                        <div className="dash-ask__choices">
                                                            <Button size="sm" variant="primary" trailingIcon={<Icon glyph={ICONS.chevronRight} size="xs" />}
                                                                onClick={() => setProfileSeed({ contractNo: k.no, identity: k.identity, name: k.customer, phone: k.phone, branch: k.branch })}>
                                                                Customer profile
                                                            </Button>
                                                            <Button size="sm" variant="secondary" trailingIcon={<Icon glyph={ICONS.chevronRight} size="xs" />}
                                                                onClick={() => pickOption({ action: k.airport ? `/airport-dashboard?search=${enc}` : `/contracts?search=${enc}` })}>
                                                                {k.airport ? 'Open in Airport' : 'Open in Monthly management'}
                                                            </Button>
                                                            {aiResponse.canEngine && (
                                                                // golden yellow: The Engine stands out for whoever uses it (owner, 12 Oct 2026)
                                                                <Button size="sm" variant="secondary" className="dash-ask__engine" leadingIcon={<Icon glyph={ICONS.engine} size="xs" />}
                                                                    trailingIcon={<Icon glyph={ICONS.chevronRight} size="xs" />}
                                                                    onClick={() => pickOption({ action: `/the-engine?tab=customer&q=${enc}` })}>
                                                                    The Engine
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                    {aiResponse.type === 'carContract' && aiResponse.options?.length > 0 && (
                                        <div className="dash-ask__more">Search further for this car:</div>
                                    )}
                                    {aiResponse.type === 'customers' && (
                                        <ul className="dash-ask__customers">
                                            {aiResponse.customers.map((c) => (
                                                <li key={c.key}>
                                                    <button type="button" className="dash-ask__customer" onClick={() => setProfileSeed(c.seed)}>
                                                        <Icon glyph={ICONS.users} size="sm" />
                                                        <span className="dash-ask__customer-text">
                                                            <span className="dash-ask__customer-name">{c.name || 'Customer'}</span>
                                                            <span className="dash-ask__customer-sub">
                                                                {[c.phone && formatPhone(c.phone), `${c.count} contract${c.count === 1 ? '' : 's'}`, c.branch, c.latest && c.latest.d && `last ${String(c.latest.d).slice(0, 10)}`].filter(Boolean).join(' · ')}
                                                            </span>
                                                        </span>
                                                        {c.open && <StatusBadge status="open" label="Open contract" />}
                                                        <Icon glyph={ICONS.chevronRight} size="xs" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {(aiResponse.type === 'choice' || aiResponse.type === 'customers' || aiResponse.type === 'carContract') && aiResponse.options && aiResponse.options.length > 0 && (
                                        <div className="dash-ask__choices">
                                            {aiResponse.options.map((opt, idx) => (
                                                <Button
                                                    key={idx}
                                                    size="sm"
                                                    variant={opt.seed ? 'primary' : 'secondary'}
                                                    trailingIcon={<Icon glyph={ICONS.chevronRight} size="xs" />}
                                                    onClick={() => pickOption(opt)}
                                                >
                                                    {opt.label}
                                                </Button>
                                            ))}
                                        </div>
                                    )}
                                    <div>
                                        <Button size="xs" variant="ghost" onClick={() => setAiResponse(null)}>Clear</Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
            <style>{`
                .dash-ask { padding: var(--space-4); display: flex; flex-direction: column; gap: var(--space-3); }
                .dash-ask__form { display: flex; gap: var(--space-2); }
                .dash-ask__answer { display: flex; flex-direction: column; gap: var(--space-2); }
                .dash-ask__text { display: flex; align-items: center; gap: 8px; font-size: var(--text-sm); color: var(--text-2); background: var(--surface-2); border-radius: var(--radius-md); padding: 10px 12px; }
                .dash-ask__text.is-error { color: var(--danger); background: var(--danger-soft); }
                .dash-ask__html { font-size: var(--text-sm); line-height: 1.6; color: var(--text); background: var(--surface-2); border-radius: var(--radius-md); padding: 10px 12px; max-height: 50vh; overflow-y: auto; white-space: pre-wrap; text-align: start; }
                .dash-ask__choices { display: flex; flex-wrap: wrap; gap: var(--space-2); }
                .dash-ask__customers { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
                .dash-ask__customer { width: 100%; display: flex; align-items: center; gap: 10px; padding: 8px 12px; text-align: start; background: var(--glass-bg); border: var(--control-border-width-sm) solid var(--control-border); border-radius: var(--radius-md); cursor: pointer; color: var(--accent-text); }
                .dash-ask__customer:hover { border-color: var(--card-hover-border); background: rgb(var(--accent-50)); }
                .dash-ask__customer-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
                .dash-ask__customer-name { font-weight: 600; font-size: var(--text-sm); color: var(--text); }
                .dash-ask__customer-sub { font-size: var(--text-xs); color: var(--text-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .dash-ask__cars { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-2); }
                .dash-ask__car { display: flex; flex-direction: column; gap: 4px; padding: 12px 14px; border-radius: var(--radius-md); background: var(--surface-2); border: var(--control-border-width-sm) solid var(--control-border); }
                .dash-ask__car.is-open { background: rgb(var(--accent-50)); border: 2px solid rgb(var(--accent-500)); }
                .dash-ask__car-head { display: flex; align-items: center; gap: 8px; color: var(--accent-text); }
                .dash-ask__car-plate { font-weight: 700; font-size: var(--text-base); color: var(--text); letter-spacing: 0.02em; }
                .dash-ask__car-label { font-size: var(--text-xs); font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: 0.04em; margin-top: 2px; }
                .dash-ask__car-no { font-family: var(--font-mono, monospace); font-weight: 700; font-size: var(--text-lg); color: var(--text); }
                .dash-ask__car-line { font-size: var(--text-sm); color: var(--text-2); }
                .dash-ask__car .dash-ask__choices { margin-top: 6px; }
                .dash-ask__more { font-size: var(--text-xs); color: var(--text-3); }
                .dash-ask__wa { color: #128c4b; font-weight: 700; text-decoration: none; }
                .dash-ask__wa:hover { color: #0b6b38; text-decoration: underline; }
                .dash-ask .yelo-btn.dash-ask__engine { background: rgb(var(--brand-gold) / 0.38); border: 2px solid rgb(var(--accent-600)); color: #1f1600; font-weight: 700; }
                .dash-ask .yelo-btn.dash-ask__engine:hover:not(:disabled) { background: rgb(var(--brand-gold) / 0.55); border-color: rgb(var(--accent-700)); color: #1f1600; }
            `}</style>
            <CustomerProfileModal open={!!profileSeed} seed={profileSeed} onClose={() => setProfileSeed(null)} onCopy={(msg) => showToast(msg || 'Copied', 'success')} />
        </>
    );
}
